import { useEffect, useRef, useState } from 'react'
import { observer } from 'mobx-react-lite'
import clsx from 'clsx'
import { useRunState } from '@/src/game/provider/useRunState'
import { useSteleBreakpoint } from '@/src/hooks/useBreakpoint'
import TextHeadline from '../ui/texts/TextHeadline'
import { playSound } from '@/src/utils/soundPlayer'
import LevelIndicatorBar from '../ui/gameElements/LevelIndicatorBar'
import { getPhaseTransitionIconsClassName, getPhaseTransitionIconsStyle } from './phaseTransitionPopUpLayout'

const phaseNames = ['Playground', 'Classroom', 'Workzone', 'Glowup'] as const
const DEBUG_FORCE_PHASE_POPUP = false
const DEBUG_FORCE_PHASE = 1

const EXIT_DURATION_MS = 600
const START_PHASE_SHOW_MS = 4400
// ~400ms before animate-level-transition name done:
const LEVEL_NAME_SOUND_DELAY_MS = 1200

type PhaseTransitionPopUpProps = {
    onStartPhaseActiveChange?: (active: boolean) => void
}

const PhaseTransitionPopUp = observer(function PhaseTransitionPopUp({
    onStartPhaseActiveChange,
}: PhaseTransitionPopUpProps) {
    const runState = useRunState()
    const isStele = useSteleBreakpoint()

    const { activeRegion, activeTransition } = runState.trackRegionState

    const [displayPhase, setDisplayPhase] = useState<number | null>(null)
    const [isExiting, setIsExiting] = useState(false)
    const showingRef = useRef(false)
    const previousRunPhaseRef = useRef(runState.phase)
    const startPhaseShownRef = useRef(false)
    const startPhaseTimeoutRef = useRef<number | null>(null)
    const exitTimeoutRef = useRef<number | null>(null)
    const levelNameSoundTimeoutRef = useRef<number | null>(null)
    const previousDisplayPhaseRef = useRef<number | null>(null)

    const clearLevelNameSoundTimeout = () => {
        if (levelNameSoundTimeoutRef.current !== null) {
            window.clearTimeout(levelNameSoundTimeoutRef.current)
            levelNameSoundTimeoutRef.current = null
        }
    }

    useEffect(() => {
        if (
            displayPhase !== null &&
            !isExiting &&
            displayPhase !== previousDisplayPhaseRef.current
        ) {
            clearLevelNameSoundTimeout()
            levelNameSoundTimeoutRef.current = window.setTimeout(() => {
                playSound('levelName')
                levelNameSoundTimeoutRef.current = null
            }, LEVEL_NAME_SOUND_DELAY_MS)
        }

        if (isExiting || displayPhase === null) {
            clearLevelNameSoundTimeout()
        }

        previousDisplayPhaseRef.current = displayPhase
    }, [displayPhase, isExiting])

    useEffect(() => {
        if (activeTransition) {
            if (startPhaseTimeoutRef.current !== null) {
                window.clearTimeout(startPhaseTimeoutRef.current)
                startPhaseTimeoutRef.current = null
            }
            if (exitTimeoutRef.current !== null) {
                window.clearTimeout(exitTimeoutRef.current)
                exitTimeoutRef.current = null
            }
            showingRef.current = true
            setIsExiting(false)
            setDisplayPhase(Math.min(activeRegion + 1, phaseNames.length))
            return
        }

        if (showingRef.current) {
            showingRef.current = false
            setIsExiting(true)
            exitTimeoutRef.current = window.setTimeout(() => {
                setDisplayPhase(null)
                setIsExiting(false)
                exitTimeoutRef.current = null
            }, EXIT_DURATION_MS)
        }
    }, [activeTransition, activeRegion])

    useEffect(() => {
        const previousRunPhase = previousRunPhaseRef.current
        const currentRunPhase = runState.phase

        if (currentRunPhase === 'idle') {
            startPhaseShownRef.current = false
        }

        if (
            previousRunPhase === 'idle' &&
            currentRunPhase === 'running' &&
            !startPhaseShownRef.current &&
            !activeTransition
        ) {
            if (startPhaseTimeoutRef.current !== null) {
                window.clearTimeout(startPhaseTimeoutRef.current)
            }
            if (exitTimeoutRef.current !== null) {
                window.clearTimeout(exitTimeoutRef.current)
                exitTimeoutRef.current = null
            }

            startPhaseShownRef.current = true
            showingRef.current = true
            setIsExiting(false)
            setDisplayPhase(1)
            onStartPhaseActiveChange?.(true)

            startPhaseTimeoutRef.current = window.setTimeout(() => {
                showingRef.current = false
                setIsExiting(true)
                exitTimeoutRef.current = window.setTimeout(() => {
                    setDisplayPhase(null)
                    setIsExiting(false)
                    onStartPhaseActiveChange?.(false)
                    exitTimeoutRef.current = null
                }, EXIT_DURATION_MS)
                startPhaseTimeoutRef.current = null
            }, START_PHASE_SHOW_MS)
        }

        previousRunPhaseRef.current = currentRunPhase
    }, [activeTransition, onStartPhaseActiveChange, runState.phase])

    useEffect(() => {
        return () => {
            onStartPhaseActiveChange?.(false)
            if (startPhaseTimeoutRef.current !== null) {
                window.clearTimeout(startPhaseTimeoutRef.current)
            }
            if (exitTimeoutRef.current !== null) {
                window.clearTimeout(exitTimeoutRef.current)
            }
            clearLevelNameSoundTimeout()
        }
    }, [onStartPhaseActiveChange])

    if (!DEBUG_FORCE_PHASE_POPUP && displayPhase === null) {
        return null
    }

    const effectiveDisplayPhase = DEBUG_FORCE_PHASE_POPUP
        ? DEBUG_FORCE_PHASE
        : displayPhase

    if (effectiveDisplayPhase === null) {
        return null
    }

    const nextPhaseName = phaseNames[effectiveDisplayPhase - 1]
    const phaseAnimationClass = clsx(
        DEBUG_FORCE_PHASE_POPUP
            ? ''
            : isExiting
                ? 'animate-level-transition-reverse'
                : 'animate-level-transition',
    )

    const completedStages = Math.max(effectiveDisplayPhase - 1, 0)

    return (
        <div className='z-20 absolute inset-0 flex justify-center items-center pointer-events-none'>
            <div
                key={`${effectiveDisplayPhase}-${isExiting ? 'out' : 'in'}`}
                className={clsx(
                    'flex flex-col justify-center items-center',
                    phaseAnimationClass,
                )}
            >
                <div
                    className={clsx(
                        getPhaseTransitionIconsClassName(isStele, 'hud'),
                        phaseAnimationClass,
                        'relative mb-10 stele:mb-40 flex items-center justify-center w-full'
                    )}
                    style={getPhaseTransitionIconsStyle(isStele, 'hud')}
                >
                    <LevelIndicatorBar
                        completedStages={completedStages}
                        showChameleIndicator
                        className={clsx(
                            'w-51 h-auto',
                            'stele:w-206'
                        )}
                    />
                </div>
                <TextHeadline fontSize={isStele ? 64 : 16} leading={0} colorPreset='red'>
                    {`Phase ${effectiveDisplayPhase}`}
                </TextHeadline>
                <TextHeadline fontSize={isStele ? 128 : 32} leading={1} colorPreset='red'>
                    {nextPhaseName}
                </TextHeadline>
            </div>
        </div>
    )
})

export default PhaseTransitionPopUp

